import { ConfigProvider, Flex, Table } from 'antd';
import { useAccess } from '@sitemax/smx-permission';
import { useCallback, useRef, useState, useEffect } from 'react';
import { TableRowSelection } from 'antd/es/table/interface';
import { MenuItemType } from 'antd/es/menu/interface';

import { useModal } from 'hooks/useModal';
import { useLoadingDocumentsPage } from 'hooks/useLoadingDocumentsPage';
import { Category, Document } from 'interfaces/documents';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { apryseExtensions, CATEGORY_TYPE, DOCUMENT_TYPE, isCategory, isDocument } from '../../../utilities/constants';
import { setDocument } from 'slices/documentSlice';
import { fetchDocument, refreshDocumentTreeLevels } from 'slices/documentsActions';
import { useModeDocumentsPage } from '../../../hooks/useModeDocumentsPage';
import { useDocumentsNavigateTo } from '../../../hooks/useDocumentsNavigateTo';
import { ModeDocumentsPageType } from '../../../types';
import './BaseTableDocuments.css';
import { useResizableColumnTableDocuments } from '../../../hooks/useResizableColumnTableDocuments';
import { useDocumentsDragAndDrop } from 'hooks/useDocumentsDragAndDrop';
import { useFetchDocumentsPage } from 'hooks/useFetchDocumentsPage';

const BaseTableDocuments = ({
    setSelectedItems,
    selected,
    modeDocument,
    fetchData,
    draggable = false,
    filterActions,
}: {
    setSelectedItems: (items: (Category | Document)[]) => void;
    selected: (Category | Document)[];
    modeDocument: ModeDocumentsPageType;
    fetchData: () => void;
    draggable?: boolean;
    filterActions?: (actions: MenuItemType[]) => MenuItemType[];
}) => {
    const canManage = useAccess(['files.all.manage']);
    const { listDocuments } = useAppSelector((state) => state.documents);
    const [load, setLoad] = useState(false);
    const loading = useLoadingDocumentsPage({ mode: modeDocument });
    const tableRef = useRef<HTMLDivElement>(null);
    const { categoryId } = useFetchDocumentsPage(modeDocument);
    const [dropTargetId, setDropTargetId] = useState<string | number | null>(null);
    const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const { breadcrumbs } = useAppSelector((state) => state.documents);

    useEffect(() => {
        if (!breadcrumbs?.length) return;
        const currentCategory = breadcrumbs[breadcrumbs.length - 1];
        setCurrentCategory({
            id: currentCategory.id,
            name: currentCategory.name,
            type: 'category',
        } as Category);
    }, [breadcrumbs]);

    useEffect(() => {
        setCurrentPage(1);
    }, [categoryId]);

    const { handleMouseDown, columnsWithResize } = useResizableColumnTableDocuments(modeDocument, tableRef, draggable, filterActions);

    const ResizableTitle = useCallback(
        (props: any) => {
            const { onResize, width, columnIndex, ...restProps } = props;

            if (!width || columnIndex === undefined) {
                return <th {...restProps} />;
            }

            return (
                <th {...restProps} style={{ position: 'relative', width }}>
                    {props.children}
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleMouseDown(e, columnIndex)}
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: -5,
                            bottom: 0,
                            width: 10,
                            cursor: 'col-resize',
                            zIndex: 1,
                            background: 'transparent',
                        }}
                    />
                </th>
            );
        },
        [handleMouseDown],
    );

    const rowSelection: TableRowSelection<Category | Document> = {
        selectedRowKeys: selected.map((item) => item.id),
        onChange: (selected, selectedRows, changeRows) => {
            setSelectedItems(selectedRows);
        },
        onSelect: (record, selected, selectedRows) => {
            setSelectedItems(selectedRows);
        },
        columnWidth: 48,
    };

    const dispatch = useAppDispatch();
    const { openModal: openDocumentMarkupModal } = useModal();
    const { isDeleted } = useModeDocumentsPage(modeDocument);
    const { navigateTo } = useDocumentsNavigateTo(modeDocument);

    const handleDocumentOpen = useCallback(
        async (record: Document) => {
            if (isDocument(record)) {
                if (apryseExtensions.includes(record.extension)) {
                    dispatch(setDocument(record));
                    dispatch(fetchDocument(record.id));
                    openDocumentMarkupModal('documentMarkupModal', {
                        readonly: isDeleted,
                    }, {
                        onSubmit: () => {
                            fetchData();
                        }
                    });
                } else {
                    const results = await dispatch(fetchDocument(record?.id));
                    const url = results.payload?.data?.url;
                    if (url) {
                        window.open(url, '_blank');
                    }
                }
            }
        },
        [dispatch, openDocumentMarkupModal, isDeleted],
    );

    const handleCategoryOpen = useCallback(
        (record: Category) => {
            if (isCategory(record)) {
                navigateTo(record.id);
            }
        },
        [navigateTo],
    );

    const onRow = useCallback(
        (record: Category | Document) => {
            return {
                onClick: () => {
                    if (modeDocument === 'deleted') {
                        return;
                    }
                    setLoad(true);
                    setTimeout(() => {
                        setLoad(false);
                    }, 1000);

                    if (isCategory(record)) {
                        handleCategoryOpen(record as Category);
                    } else if (isDocument(record)) {
                        handleDocumentOpen(record as Document);
                    }
                },
                className: isCategory(record) && dropTargetId == record.id ? 'drop-target' : '',
            };
        },
        [handleCategoryOpen, handleDocumentOpen, selected, fetchData, dispatch, dropTargetId, canManage],
    );

    const { drop } = useDocumentsDragAndDrop(currentCategory, {
        onMove: (affectedCategories) => {
            dispatch(refreshDocumentTreeLevels(affectedCategories));
            fetchData();
        },
    });
    drop(tableRef.current);

    return (
        <Flex ref={tableRef} className="resizable-table-container">
            <ConfigProvider
                theme={{
                    components: {
                        Table: {
                            // cellPaddingBlock: 8,
                        },
                    },
                }}
            >
                <Table
                    columns={columnsWithResize}
                    dataSource={listDocuments}
                    loading={load || loading}
                    rowSelection={rowSelection}
                    onRow={onRow}
                    rowKey="id"
                    components={{
                        header: {
                            cell: ResizableTitle,
                        },
                    }}
                    scroll={{ y: 'calc(100vh - 436px)' }}
                    pagination={{
                        current: currentPage,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        onChange: (page) => {
                            setCurrentPage(page);
                        },
                    }}
                    tableLayout="fixed"
                />
            </ConfigProvider>
        </Flex>
    );
};

export default BaseTableDocuments;
